import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const defaultIcon = L.icon({
 iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
 iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
 shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
 iconSize: [25, 41],
 iconAnchor: [12, 41],
 popupAnchor: [1, -34],
 shadowSize: [41, 41],
});

interface InteractiveMapProps {
 latitude: number | null;
 longitude: number | null;
 onLocationChange: (lat: number, lng: number) => void;
 draggable?: boolean;
}

function MapUpdater({ latitude, longitude }: { latitude: number | null; longitude: number | null }) {
 const map = useMap();

 useEffect(() => {
 if (latitude !== null && longitude !== null) {
 map.setView([latitude, longitude], 13);
 }
 }, [latitude, longitude, map]);

 return null;
}

function DraggableMarker({ 
 position, 
 onDragEnd,
 draggable 
}: { 
 position: [number, number] | null;
 onDragEnd: (lat: number, lng: number) => void;
 draggable: boolean;
}) {
 const markerRef = useRef<L.Marker>(null);

 useMapEvents({
 click(e) {
 if (draggable) {
 onDragEnd(e.latlng.lat, e.latlng.lng);
 }
 },
 });

 useEffect(() => {
 const marker = markerRef.current;
 if (marker) {
 marker.on('dragend', () => {
 const position = marker.getLatLng();
 onDragEnd(position.lat, position.lng);
 });
 }
 }, [onDragEnd]);

 if (!position) return null;

 return (
 <Marker
 ref={markerRef}
 position={position}
 icon={defaultIcon}
 draggable={draggable}
 />
 );
}

export function InteractiveMap({ 
 latitude, 
 longitude, 
 onLocationChange,
 draggable = true 
}: InteractiveMapProps) {
 const position: [number, number] | null = 
 latitude !== null && longitude !== null ? [latitude, longitude] : null;

 const center: [number, number] = position || [39.8283, -98.5795];

 return (
 <div className="relative">
 <MapContainer
 center={center}
 zoom={position ? 13 : 4}
 style={{ height: '400px', width: '100%', borderRadius: '8px' }}
 scrollWheelZoom={true}
 >
 <TileLayer
 attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
 url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
 />
 
 <MapUpdater latitude={latitude} longitude={longitude} />
 
 <DraggableMarker
 position={position}
 onDragEnd={onLocationChange}
 draggable={draggable}
 />
 </MapContainer>

 {draggable && position && (
 <div className="mt-2 text-sm text-gray-600">
 💡 Tip: Click anywhere on the map or drag the marker to adjust the location
 </div>
 )}
 </div>
 );
}
